import { isNeuropsychEvaluationProgram } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { Episode, EpisodeStates } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { Note, NoteStatusStates, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'

import { LICENSED_NP_ROLES } from './asyncTimeNote'
import { hasRole } from '../../../common/utils/utils'

export const DISCHARGE_NOTE_NAME = 'npDischargeNote'

type NoteRef = Pick<Note, 'episode_id' | 'note_type' | 'status'>

const isEpisodeDischargeNote = (note: NoteRef, episodeId?: string) =>
  note.episode_id === episodeId && note.note_type === NoteTypes.DISCHARGE

export const findEpisodeDischargeDraft = <T extends NoteRef>(notes: T[], episodeId?: string): T | undefined =>
  notes.find((note) => isEpisodeDischargeNote(note, episodeId) && note.status === NoteStatusStates.DRAFT)

const hasSignedDischargeNote = (notes: NoteRef[], episodeId?: string) =>
  notes.some((note) => isEpisodeDischargeNote(note, episodeId) && note.status === NoteStatusStates.COMPLETED)

// The episode stays in progress until the deferred closure runs, so a signed Discharge Note must hide the entry:
// a second one would push the closure out again. A draft stays reachable so the provider can finish it.
export const canAuthorDischargeNote = ({
  isNPTestingEnabled,
  episode,
  roles,
  userId,
  notes,
  areNotesAvailable,
}: {
  isNPTestingEnabled?: boolean
  episode?: Pick<Episode, 'id' | 'program_name' | 'state' | 'provider_id'>
  roles: string[]
  userId?: string
  notes: NoteRef[]
  // Without the episode's notes there is no way to know whether it already has a Discharge Note.
  areNotesAvailable: boolean
}): boolean =>
  !!isNPTestingEnabled &&
  areNotesAvailable &&
  !!episode &&
  isNeuropsychEvaluationProgram(episode.program_name) &&
  episode.state === EpisodeStates.IN_PROGRESS &&
  hasRole(roles, LICENSED_NP_ROLES) &&
  // Only the assigned provider can author it: the server rejects the rest.
  (!episode.provider_id || episode.provider_id === userId) &&
  !hasSignedDischargeNote(notes, episode.id)
